/* Volatile — horizontal-carousel position indicator + INFINITE LOOP.
 *
 * Any `[data-card-scroll]` element is treated as a horizontal scroll-
 * snap carousel of `.v-card__scroll-img` children (or similar). On
 * init we clone the FIRST and LAST slide and prepend / append them so
 * the user can scroll past either end and see "the next" image — when
 * scroll lands on a clone, we instantly snap (without animation) to
 * the matching real slide. Net effect: swiping right past the last
 * image lands on the first; swiping left past the first lands on the
 * last. Same behaviour for the click hover-arrows.
 *
 * The position indicator (`[data-card-dots]`, single thumb or legacy
 * segmented bar) reflects the REAL slide index (0..count-1), not the
 * raw scroll position which includes the prepended last-clone.
 *
 * Also re-runs automatically when new scroll containers are inserted
 * (search overlay results, AJAX collection nav, etc.).
 */
(() => {
  /* ── Deferred slides ───────────────────────────────────────────────
     `snippets/product-card.liquid` renders slides 2-4 of every card with
     the real candidate list parked on `data-srcset` and a transparent 1x1
     GIF in `src`, so they cost nothing until someone actually reaches for
     them. This is what promotes them.

     Aditya, 2026-09-14: "nothing which is not being shown is supposed to
     be loaded". `loading="lazy"` does not deliver that inside a horizontal
     strip - the browser counts the whole track as in-viewport and pulls
     every slide of every card that has scrolled into vertical range. On the
     mobile home rail that was three wasted images per card for slides the
     rail will not even let you reach.

     Hydration is keyed to INTERACTION rather than to proximity, so a card
     sitting on screen untouched still costs exactly one image:
       • pointerover - the desktop hover, which also drives the hover-seek
         to image two, so the fetch starts before the scroll animation does
       • focusin     - keyboard equivalent of the above
       • a chevron   - handled where the click is, further down
       • touchstart  - but ONLY on a strip the visitor can actually swipe.
         The rail freezes its cards with `overflow: hidden` on phones, so a
         plain tap there must NOT pull three images that cannot be shown.

     The whole card is hydrated at once, clones included: card-carousel
     deep-clones the first and last slide for the infinite loop, so those
     copies carry `data-srcset` too and would otherwise stay blank when the
     visitor wraps around the ends. */
  const hydrateCard = (el) => {
    const card = el && el.closest ? el.closest('.v-card') : null;
    const scope = card || el;
    if (!scope || !scope.querySelectorAll || scope.__vHydrated) return;
    scope.__vHydrated = true;
    scope.querySelectorAll('img[data-srcset]').forEach((img) => {
      const set = img.getAttribute('data-srcset');
      const src = img.getAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
      img.removeAttribute('data-card-defer');
      /* srcset first: setting src on its own would fire a request for the
         fallback that srcset is about to supersede. */
      if (set) img.setAttribute('srcset', set);
      if (src) img.setAttribute('src', src);
    });
  };

  const isSwipeable = (el) => {
    const strip = el && el.closest ? el.closest('[data-card-scroll]') : null;
    if (!strip) return false;
    const ox = getComputedStyle(strip).overflowX;
    return ox !== 'hidden' && ox !== 'visible';
  };

  document.addEventListener('pointerover', (e) => {
    /* Ignore the synthetic pointerover a tap emits on touch hardware;
       that path is the touchstart listener's, with its swipeable test. */
    if (e.pointerType === 'touch') return;
    hydrateCard(e.target);
  }, { passive: true, capture: true });

  document.addEventListener('focusin', (e) => hydrateCard(e.target), { passive: true });

  document.addEventListener('touchstart', (e) => {
    if (isSwipeable(e.target)) hydrateCard(e.target);
  }, { passive: true, capture: true });

  /* ── Desktop slide-2 preload ──────────────────────────────────────
     Aditya, 2026-09-21: "when I hover, the second image isnt loading
     instantly". Hydrating on pointerover means the download STARTS at
     hover, so the hover-seek always beat it. On hover-capable devices we
     now promote just the SECOND real slide of each card once it comes
     within 400px of the viewport, at fetchpriority=low so it never
     competes with slide 1. Slides 3-4 remain interaction-only, and touch
     devices (no hover, rail can't even reach slide 2) load nothing extra:
     the 2026-09-14 "nothing which is not shown is loaded" rule still
     holds there. */
  const promoteImg = (img) => {
    if (!img) return;
    const set = img.getAttribute('data-srcset');
    const src = img.getAttribute('data-src');
    if (!set && !src) return;
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
    img.removeAttribute('data-card-defer');
    img.setAttribute('fetchpriority', 'low');
    if (set) img.setAttribute('srcset', set);
    if (src) img.setAttribute('src', src);
  };
  const hoverCapable = window.matchMedia
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const slide2Preload = hoverCapable && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          const real = Array.from(entry.target.children)
            .filter((c) => !c.dataset || !c.dataset.carouselClone);
          const slide2 = real[1];
          if (!slide2) return;
          promoteImg(slide2.matches && slide2.matches('img') ? slide2
            : slide2.querySelector && slide2.querySelector('img[data-srcset]'));
        });
      }, { rootMargin: '400px' })
    : null;
  const watchSlide2 = (scrollEl) => { if (slide2Preload) slide2Preload.observe(scrollEl); };

  const findDots = (scrollEl) => {
    if (!scrollEl) return null;
    if (scrollEl.parentElement) {
      const sibling = scrollEl.parentElement.querySelector(':scope > [data-card-dots]');
      if (sibling) return sibling;
    }
    return scrollEl.querySelector('[data-card-dots]');
  };

  /* ── Re-anchor on viewport change ──────────────────────────────────
     The initial placement runs once and then stops: `seeked` latches true
     and the ResizeObserver that was watching for late layout disconnects.
     Nothing re-asserted the position after that, so ANY change to the
     card's width - resizing the window, browser zoom, a phone rotating,
     the desktop/tablet/mobile column count changing - left `scrollLeft` at
     a pixel value computed for the OLD width. At 1440px a card is 360 wide
     and rests at 360; widen to 1902 and the card is 476 wide but still
     sits at 360, which is 0.76 of a slide: every card in the grid shows
     the right-hand slice of one image beside the left-hand slice of the
     next. Chromium's mandatory scroll-snap papers over it by re-snapping,
     Safari does not, so the grid stays split until something scrolls.
     (Aditya, 2026-09-13, on a full-width grab of the listing grid.)

     Fix: remember which slide the card is on and put it back on that same
     slide against the NEW width. Debounced because resize fires in a
     stream, and skipped while a card is mid clone-snap. */
  let reanchorTimer = null;
  const reanchorAll = () => {
    document.querySelectorAll('.v-card__scroll').forEach((scrollEl) => {
      const w = scrollEl.clientWidth;
      if (!w) return;
      const idx = typeof scrollEl.__vRawIdx === 'number'
        ? scrollEl.__vRawIdx
        : Math.round(scrollEl.scrollLeft / w);
      const target = idx * w;
      if (Math.abs(scrollEl.scrollLeft - target) < 1) return;
      const prevBehavior = scrollEl.style.scrollBehavior;
      const prevSnap = scrollEl.style.scrollSnapType;
      scrollEl.style.scrollBehavior = 'auto';
      scrollEl.style.scrollSnapType = 'none';
      scrollEl.scrollLeft = target;
      // eslint-disable-next-line no-unused-expressions
      scrollEl.offsetWidth;
      scrollEl.style.scrollBehavior = prevBehavior;
      scrollEl.style.scrollSnapType = prevSnap;
    });
  };
  const scheduleReanchor = () => {
    window.clearTimeout(reanchorTimer);
    reanchorTimer = window.setTimeout(reanchorAll, 120);
  };
  window.addEventListener('resize', scheduleReanchor, { passive: true });
  window.addEventListener('orientationchange', scheduleReanchor, { passive: true });
  /* bfcache restores replay the old scroll offsets against a viewport that
     may have changed while the page was away. */
  window.addEventListener('pageshow', scheduleReanchor);

  const init = (scrollEl) => {
    if (!scrollEl || scrollEl.__vCarouselInited) return;
    scrollEl.__vCarouselInited = true;
    watchSlide2(scrollEl);
    const dots = findDots(scrollEl);

    /* Real slides at init-time — children of the scroll container
       BEFORE we splice in clones. Capture once so the clone-detect
       logic never confuses a clone for an original. */
    const realSlides = Array.from(scrollEl.children);
    const count = realSlides.length;

    /* ── Infinite loop via cloning. Only when there are >1 real
          slides (single-image cards have nothing to loop). ── */
    let cloned = false;
    if (count > 1) {
      const firstClone = realSlides[0].cloneNode(true);
      firstClone.setAttribute('aria-hidden', 'true');
      firstClone.dataset.carouselClone = 'first';
      const lastClone = realSlides[count - 1].cloneNode(true);
      lastClone.setAttribute('aria-hidden', 'true');
      lastClone.dataset.carouselClone = 'last';
      scrollEl.insertBefore(lastClone, realSlides[0]);
      scrollEl.appendChild(firstClone);
      cloned = true;

      /* Position at the first REAL slide (skip the prepended last-clone).

         This used to be a single `requestAnimationFrame` that bailed on
         `if (!w) return`. That bail is why cards sometimes opened on the
         LAST image. If `clientWidth` was still 0 on that one frame — card
         below the fold with lazy images, an AJAX collection swap, a slow
         first layout — the seek was silently skipped and the scroller
         stayed at scrollLeft 0, which IS the prepended last-slide clone.
         Then the first scroll event ran `cloneSnap`, which reads rawIdx 0
         as "the user wrapped backwards past the first slide" and seeks to
         the real LAST slide. One missed frame, and the card is showing the
         last image and looks like it rotated there by itself.

         So: keep trying until the seek actually takes, from three angles —
         rAF retries for the ordinary slow-layout case, a ResizeObserver for
         width that arrives much later, and image `load` for reflow after
         decode. All of them stop the moment the user touches the carousel,
         so nothing ever yanks a slide out from under a real swipe. */
      let seeked = false;
      let userMoved = false;

      const seekFirst = () => {
        if (seeked || userMoved) return true;
        const w = scrollEl.clientWidth;
        if (!w) return false;
        const prev = scrollEl.style.scrollBehavior;
        const prevSnap = scrollEl.style.scrollSnapType;
        scrollEl.style.scrollBehavior = 'auto';
        // scroll-snap fights a manual scrollLeft and can pull it back to
        // the nearest snap point, same reason cloneSnap disables it.
        scrollEl.style.scrollSnapType = 'none';
        scrollEl.scrollLeft = w;
        // eslint-disable-next-line no-unused-expressions
        scrollEl.offsetWidth;
        scrollEl.style.scrollBehavior = prev;
        scrollEl.style.scrollSnapType = prevSnap;
        // Only call it done if the position actually stuck.
        if (Math.abs(scrollEl.scrollLeft - w) > 1) return false;
        seeked = true;
        return true;
      };
      scrollEl.__vSeeked = () => seeked;

      ['pointerdown', 'touchstart', 'keydown'].forEach((evt) => {
        scrollEl.addEventListener(evt, () => { userMoved = true; }, { passive: true, once: true });
      });
      /* `wheel` used to be in that list, which meant scrolling the PAGE with
         the pointer resting over a card permanently marked that card as
         "the user is driving" and disabled every later correction. Only a
         horizontal wheel is actually aimed at this carousel. */
      scrollEl.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) userMoved = true;
      }, { passive: true });

      let tries = 0;
      const tick = () => {
        if (seekFirst() || ++tries > 90) return;
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);

      if (window.ResizeObserver) {
        const seekRo = new ResizeObserver(() => {
          if (seekFirst()) seekRo.disconnect();
        });
        seekRo.observe(scrollEl);
      }

      scrollEl.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', () => {
          // A late decode can reflow the row and drift the offset. Re-assert
          // once, but never after the user has taken over.
          if (userMoved) return;
          seeked = false;
          seekFirst();
        }, { once: true });
      });
    }

    /* Slider thumb / segmented bar setup — uses REAL count, ignores clones. */
    const thumb = dots ? dots.querySelector('.v-card__progress-thumb') : null;
    const segments = (dots && !thumb) ? Array.from(dots.children) : null;
    const indicatorCount = thumb
      ? Number((dots && dots.dataset.imageCount) || 0)
      : (segments ? segments.length : 0);
    if (thumb && indicatorCount > 1) {
      thumb.style.width = (100 / indicatorCount) + '%';
    }

    /* ── Two listeners:
         • `scroll`     — updates the indicator thumb continuously.
         • `scrollend`  (or a settle-detect fallback) — fires the
                         clone-to-real snap exactly ONCE when the
                         user's swipe momentum has stopped. Snapping
                         during the scroll itself is what was
                         producing the jitter the user reported —
                         the browser was mid-animation when we
                         instantly seeked, so the user saw a flicker.
                         Snapping only at scrollend is invisible. ── */
    const indicatorUpdate = () => {
      const w = scrollEl.clientWidth;
      if (!w) return;
      const rawIdx = Math.round(scrollEl.scrollLeft / w);
      /* Remember the slide so a viewport change can put the card back on
         THIS image instead of leaving it parked between two. */
      scrollEl.__vRawIdx = rawIdx;
      const realIdx = cloned
        ? ((rawIdx - 1) + indicatorCount) % indicatorCount
        : Math.min(indicatorCount - 1, rawIdx);
      if (thumb && indicatorCount > 1) {
        thumb.style.transform = 'translateX(' + (realIdx * 100) + '%)';
      } else if (segments) {
        segments.forEach((seg, idx) => {
          seg.classList.toggle('is-active', idx === realIdx);
        });
      }
    };

    let snapping = false;
    const cloneSnap = () => {
      if (!cloned || snapping) return;
      /* Do not resolve clones until the carousel has actually been placed
         on the first real slide. Without this, a scroller still parked at
         scrollLeft 0 (the last-clone) gets "corrected" to the real LAST
         slide, which is the wrong-image-on-open bug. */
      if (scrollEl.__vSeeked && !scrollEl.__vSeeked()) return;
      const w = scrollEl.clientWidth;
      if (!w) return;
      const rawIdx = Math.round(scrollEl.scrollLeft / w);
      let target = null;
      // rawIdx 0 = prepended last-clone → seek to real last (rawIdx count).
      if (rawIdx <= 0) target = w * count;
      // rawIdx count+1 = appended first-clone → seek to real first (rawIdx 1).
      else if (rawIdx >= count + 1) target = w;
      if (target == null) return;
      snapping = true;
      // Disable `scroll-snap` + smooth-scroll for the duration of the
      // instant seek. With scroll-snap active, the browser fights our
      // manual `scrollLeft` set and can pull the position back to the
      // nearest snap point of the OLD slide — that's the "hang" the
      // user reported when trying to scroll from the wrapped first
      // slide to the second. Switching snap off, seeking, then
      // re-enabling on the next frame eliminates that fight entirely. */
      const prevBehavior = scrollEl.style.scrollBehavior;
      const prevSnap = scrollEl.style.scrollSnapType;
      scrollEl.style.scrollBehavior = 'auto';
      scrollEl.style.scrollSnapType = 'none';
      scrollEl.scrollLeft = target;
      // Force a layout flush so the seek commits before scroll-snap
      // is re-enabled.
      // eslint-disable-next-line no-unused-expressions
      scrollEl.offsetWidth;
      indicatorUpdate();
      // Restore the original scroll behavior + snap-type two frames
      // later. One frame for the seek to settle, one more so the
      // next user input lands cleanly on the new position.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollEl.style.scrollBehavior = prevBehavior;
          scrollEl.style.scrollSnapType = prevSnap;
          snapping = false;
        });
      });
    };

    scrollEl.addEventListener('scroll', indicatorUpdate, { passive: true });

    // Settle-detection — snap only after the user (and any in-flight
    // smooth-scroll animation) has fully stopped. Using a debounced
    // settle window of ~180 ms across all browsers, even where
    // `scrollend` is supported, because `scrollend` on Chrome can
    // fire DURING the smooth-scroll deceleration phase, which races
    // with our seek and produces the desktop hang.
    let settleTimer;
    scrollEl.addEventListener('scroll', () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(cloneSnap, 180);
    }, { passive: true });

    if (window.ResizeObserver) {
      new ResizeObserver(indicatorUpdate).observe(scrollEl);
    }
    indicatorUpdate();
  };

  const initAll = (root) => {
    (root || document).querySelectorAll('[data-card-scroll]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Pick up scroll containers added later (search results render, AJAX nav, etc.).
  if (window.MutationObserver) {
    new MutationObserver((muts) => {
      for (const mut of muts) {
        for (const node of mut.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-card-scroll]')) init(node);
          if (node.querySelectorAll) initAll(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Hover-arrow navigation — `<span data-card-nav>` overlay inside
     each card scrolls the carousel by one slide on click. Lives
     inside the wrapping `<a>` (Volatile cards are link-wrapped), so
     we preventDefault + stopPropagation to avoid following the link
     when the user only meant to advance the carousel. The infinite-
     loop snap is handled by the scroll listener above — clicking
     forward at the last image scrolls into the first-clone, then
     gets snapped to the real first; clicking back at the first
     image scrolls into the last-clone, then snapped to the real last. */
  document.addEventListener('click', (e) => {
    const navBtn = e.target.closest('[data-card-nav]');
    if (!navBtn) return;
    e.preventDefault();
    e.stopPropagation();
    const card = navBtn.closest('.v-card');
    if (!card) return;
    /* A chevron is the one way to reach slides 2-4 on a strip that is
       frozen to swiping, so it has to promote the deferred slides itself. */
    hydrateCard(card);
    const scrollEl = card.querySelector('[data-card-scroll]');
    if (!scrollEl) return;
    const w = scrollEl.clientWidth;
    if (!w) return;
    const dir = navBtn.dataset.cardNav === 'next' ? 1 : -1;
    scrollEl.scrollBy({ left: dir * w, behavior: 'smooth' });
  });

  /* ── Quick-add: pick a size on the card, it goes in the bag ──────────
     Lives HERE rather than in its own asset for a structural reason, not
     laziness: a new file would need a <script> tag, every script tag on the
     site is in `layout/theme.liquid`, and that is a protected Magic Checkout
     file. Emitting one from `snippets/product-card.liquid` instead would
     print a tag per card - two dozen on a collection page. This file is
     already loaded once, already owns delegated clicks on `.v-card`, and is
     the natural home for it.

     Same `<span role="button">`-inside-an-`<a>` situation as the arrows
     above, so preventDefault + stopPropagation keep a size press from
     navigating to the PDP. */
  const ADD_LOCK = new WeakSet();

  document.addEventListener('click', async (e) => {
    const pill = e.target.closest('[data-card-add]');
    if (!pill) return;
    e.preventDefault();
    e.stopPropagation();

    const variantId = pill.dataset.cardAdd;
    if (!variantId || ADD_LOCK.has(pill)) return;
    ADD_LOCK.add(pill);
    pill.setAttribute('data-card-add-busy', '');

    const label = pill.dataset.cardAddLabel || pill.textContent;
    try {
      /* `VolatileCart.add()` owns the whole sequence - optimistic count,
         one add.js call that also returns the drawer section, background
         reconcile. Same path the PDP uses, so a size picked on a card and a
         size picked on the product page cost exactly the same. */
      if (window.VolatileCart && typeof window.VolatileCart.add === 'function') {
        await window.VolatileCart.add(variantId, 1);
      } else {
        const body = new FormData();
        body.set('id', String(variantId));
        body.set('quantity', '1');
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body,
        });
        if (!res.ok) throw new Error(`add.js HTTP ${res.status}`);
        await res.json();
      }

      pill.setAttribute('data-card-add-done', '');
      pill.textContent = '✓';
    } catch (err) {
      /* A sold-out-since-render variant is the common case here: the page
         may have been open a while. Put the size back and let the customer
         try another rather than leaving a stuck spinner. */
      pill.textContent = label;
      if (window.VolatileCart) { try { await window.VolatileCart.refresh(); } catch (_) {} }
    } finally {
      pill.removeAttribute('data-card-add-busy');
      window.setTimeout(() => {
        pill.removeAttribute('data-card-add-done');
        pill.textContent = label;
        ADD_LOCK.delete(pill);
      }, 1400);
    }
  });
})();
